import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { enableAdminMode, disableAdminMode } from '../utils/protection';
import { useTheme } from './ThemeContext';
import syncEngine from '../services/syncEngine';
import { installOfflineInterceptor } from '../services/offlineInterceptor';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const retryRequest = async (fn, retries = MAX_RETRIES) => {
  for (let i = 0; i <= retries; i++) {
    try {
      return await fn();
    } catch (error) {
      const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');
      const isTimeout = error.code === 'ECONNABORTED';
      if ((isNetworkError || isTimeout) && i < retries) {
        await sleep(RETRY_DELAY * Math.pow(2, i));
        continue;
      }
      throw error;
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [networkError, setNetworkError] = useState(false);
  const { loadTheme } = useTheme();

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: `${API_URL}/api`,
      timeout: 15000,
    });
    if (token) {
      instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    instance.interceptors.response.use(
      (response) => {
        setNetworkError(false);
        return response;
      },
      (error) => {
        if (!error.response && (error.code === 'ERR_NETWORK' || error.message === 'Network Error')) {
          setNetworkError(true);
        }
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    // Phase 5.4a-fix (2026-05-15) — Global offline interceptor. Adds
    // `Idempotency-Key` on writes and queues network-failed writes to the
    // IndexedDB outbox. Online behaviour is unchanged.
    try { installOfflineInterceptor(instance); } catch (_) { /* noop */ }
    return instance;
  }, [token]);

  const fetchUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await retryRequest(() => api.get('/auth/me'));
      setUser(response.data);
      setNetworkError(false);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
      } else {
        setNetworkError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [token, api]);

  useEffect(() => {
    fetchUser();
    if (token) loadTheme(token);
  }, [fetchUser]);

  // Phase 5.4a (2026-05-15) — bind the offline sync engine to the auth
  // axios instance. Detached on logout/token expiry.
  useEffect(() => {
    if (!token || !user) return;
    syncEngine.attach(api);
    return () => syncEngine.detach();
  }, [api, token, user]);

  const login = async (username, password, isClient = false) => {
    try {
      const endpoint = isClient ? '/client/login' : '/auth/login';
      const response = await api.post(endpoint, { username, password });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return { 
          success: false, 
          requires_2fa: true, 
          temp_token: response.data.temp_token 
        };
      }

      const { access_token, user: userData, must_change_pin, must_change_password, password_change_reason } = response.data;
      
      localStorage.setItem('token', access_token);
      // 2026-05 — picker forcé à chaque login : on purge la session magasin
      // mémorisée pour que TOUS les rôles (super_admin, admin_magasin,
      // vendeur, technicien) revoient le picker à chaque connexion, même
      // s'ils n'ont qu'un seul magasin assigné.
      try {
        localStorage.removeItem('pos_session_magasin_id');
        localStorage.removeItem('pos_current_magasin_id');
      } catch (_) { /* noop */ }
      if (must_change_pin) {
        localStorage.setItem('must_change_pin', 'true');
      } else {
        localStorage.removeItem('must_change_pin');
      }
      // Phase 2 §2.9 — must_change_password flag (post-deploy security campaign)
      const enrichedUser = {
        ...userData,
        must_change_password: !!must_change_password,
        password_change_reason: password_change_reason || '',
      };
      setToken(access_token);
      setUser(enrichedUser);
      loadTheme(access_token);
      
      return { success: true, user: enrichedUser };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur de connexion' 
      };
    }
  };

  const verify2FA = async (tempToken, code) => {
    try {
      const response = await api.post('/auth/2fa/check', { temp_token: tempToken, code });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      loadTheme(access_token);
      
      return { success: true, user: userData };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Code 2FA invalide'
      };
    }
  };

  const register = async (userData, isClient = false) => {
    try {
      const endpoint = isClient ? '/client/register' : '/auth/register';
      const response = await api.post(endpoint, userData);
      const { access_token, user: newUser } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(newUser);
      
      return { success: true, user: newUser };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Erreur d\'inscription' 
      };
    }
  };

  const googleLogin = async () => {
    // Phase 2 §2.1 — Google OAuth retiré. Cette fonction est conservée pour
    // compat des appelants existants mais retourne immédiatement une erreur.
    return {
      success: false,
      error: "L'authentification Google a été désactivée.",
    };
  };

  const refreshUser = useCallback(() => fetchUser(), [fetchUser]);

  // ISOLATION — remplace EFFECTIVEMENT le JWT courant après un choix de boutique
  // (POST /magasins/select-active renvoie un jeton re-signé portant sid + shop_ver +
  // active_magasin). Le nouveau jeton devient la SEULE autorité : l'axios `api` est
  // recréé (dépendance [token]) et l'ancien jeton est PÉRIMÉ côté serveur au prochain
  // switch. On ne se contente donc plus d'un indice localStorage/en-tête côté client.
  const applySessionToken = useCallback((access_token) => {
    if (!access_token) return;
    try { localStorage.setItem('token', access_token); } catch (_) { /* noop */ }
    setToken(access_token);
    try { loadTheme(access_token); } catch (_) { /* noop */ }
  }, [loadTheme]);

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  // Le super_admin (Carlos) doit pouvoir tout faire ce qu'un admin fait
  // (ex. fermeture forcée de caisse). On le traite donc comme admin ;
  // les fonctions réservées au super_admin utilisent isSuperAdmin.
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isClient = user?.role === 'client';
  const isAuthenticated = !!user;

  // Enable/disable copy-paste protection based on role
  useEffect(() => {
    // Phase 2.5 — Only carlos (super-admin owner) bypasses copy-paste lock.
    // All other admins/staff/clients/visitors are subject to clipboard protection.
    if (user?.username === 'carlos' && (isAdmin || isSuperAdmin)) {
      enableAdminMode();
    } else {
      disableAdminMode();
    }
  }, [isAdmin, isSuperAdmin, user?.username]);

  const retry = useCallback(() => {
    setLoading(true);
    setNetworkError(false);
    fetchUser();
  }, [fetchUser]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      verify2FA,
      register,
      googleLogin,
      logout,
      refreshUser,
      applySessionToken,
      isAdmin,
      isSuperAdmin,
      isClient,
      isAuthenticated,
      api,
      networkError,
      retry
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
